// Modal de nova busca de leads: nicho + cidade/UF + quantidade, com a
// estimativa contra o saldo e o aviso de repetição consultado ao sair dos
// campos (o backend também bloqueia com 409).
import React, { useEffect, useRef, useState } from "react";
import { makeStyles, alpha } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import WarningAmberIcon from "@material-ui/icons/ReportProblemOutlined";
import SearchIcon from "@material-ui/icons/Search";
import { toast } from "react-toastify";
import moment from "moment";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import AerosDialog from "../AerosDialog";
import useAerosModalStyles from "../../styles/aerosModalStyles";
import { AEROS, stateText } from "../../styles/aerosTokens";
import { estimativaBusca, formatMilhar } from "../../pages/Leads/leadsRules";

export const UFS = ["AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO"];

const useStyles = makeStyles((theme) => ({
  grid: {
    display: "grid",
    gridTemplateColumns: "minmax(0,1fr) 110px",
    gap: 12,
    [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
  },
  hint: { fontSize: 12, color: theme.palette.text.secondary, marginTop: 6 },
  hintBad: { color: stateText(theme, AEROS.error, "danger") },
  aviso: {
    display: "flex", gap: 10, alignItems: "flex-start", marginTop: 12,
    backgroundColor: alpha(AEROS.warning, 0.07), border: `1px solid ${alpha(AEROS.warning, 0.28)}`,
    borderRadius: 12, padding: "10px 14px", fontSize: 12.5, color: theme.palette.text.primary,
    "& .MuiSvgIcon-root": { fontSize: 18, color: stateText(theme, AEROS.warning, "warning"), flex: "none" },
  },
}));

const NovaBuscaLeadsModal = ({ open, onClose, onCriada, disponivel, limites = { min: 10, max: 500 }, diasBloqueio = 30 }) => {
  const classes = useStyles();
  const m = useAerosModalStyles();
  const [nicho, setNicho] = useState("");
  const [cidade, setCidade] = useState("");
  const [estado, setEstado] = useState("MG");
  const [quantidade, setQuantidade] = useState(String(limites.min));
  const [repetida, setRepetida] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const pedidoRef = useRef(0);

  useEffect(() => {
    if (!open) return;
    pedidoRef.current += 1;
    setNicho(""); setCidade(""); setEstado("MG"); setQuantidade(String(limites.min)); setRepetida(null);
  }, [open, limites.min]);

  const consultaRepeticao = async () => {
    if (nicho.trim().length < 3 || cidade.trim().length < 2) { setRepetida(null); return; }
    const meu = ++pedidoRef.current;
    try {
      const { data } = await api.get("/leads/buscas/repetida", { params: { nicho: nicho.trim(), cidade: cidade.trim(), uf: estado } });
      if (meu === pedidoRef.current) setRepetida(data?.repetida ? data.ultimaBusca : null);
    } catch (err) {
      if (meu === pedidoRef.current) setRepetida(null);
    }
  };

  const est = estimativaBusca(Number(quantidade), disponivel);
  const dentroDosLimites = est.quantidade >= limites.min && est.quantidade <= limites.max;
  const podeEnviar = nicho.trim().length >= 3 && cidade.trim().length >= 2 && est.ok && dentroDosLimites && !repetida && !enviando;

  const handleSubmit = async () => {
    if (!podeEnviar) return;
    setEnviando(true);
    try {
      const { data } = await api.post("/leads/buscas", { nicho: nicho.trim(), cidade: cidade.trim(), estado, quantidade: est.quantidade });
      toast.success(i18n.t("aeros.leads.search.created"));
      if (onCriada) onCriada(data);
      onClose();
    } catch (err) {
      if (err?.response?.status === 409) {
        const iso = String(err.response.data?.error || "").replace(/^.*em /, "");
        setRepetida(iso || new Date().toISOString());
      } else {
        toastError(err);
      }
    } finally {
      setEnviando(false);
    }
  };

  return (
    <AerosDialog
      open={open}
      onClose={() => { if (!enviando) onClose(); }}
      width={560}
      title={i18n.t("aeros.leads.search.title")}
      subtitle={i18n.t("aeros.leads.search.subtitle")}
      onSubmit={handleSubmit}
      loading={enviando}
      submitDisabled={!podeEnviar}
      submitIcon={<SearchIcon />}
      submitLabel={i18n.t("aeros.leads.search.submit")}
      cancelLabel={i18n.t("aeros.confirm.cancel")}
    >
      <label className={m.fieldLabel} htmlFor="leads-nicho">{i18n.t("aeros.leads.search.niche")}</label>
      <TextField id="leads-nicho" fullWidth variant="outlined" className={`${m.input} ${m.inputSmall}`}
        placeholder={i18n.t("aeros.leads.search.nichePlaceholder")} value={nicho}
        onChange={(e) => setNicho(e.target.value)} onBlur={consultaRepeticao} inputProps={{ maxLength: 80 }} />

      <div className={classes.grid} style={{ marginTop: 12 }}>
        <div>
          <label className={m.fieldLabel} htmlFor="leads-cidade">{i18n.t("aeros.leads.search.city")}</label>
          <TextField id="leads-cidade" fullWidth variant="outlined" className={`${m.input} ${m.inputSmall}`}
            value={cidade} onChange={(e) => setCidade(e.target.value)} onBlur={consultaRepeticao} inputProps={{ maxLength: 80 }} />
        </div>
        <div>
          <label className={m.fieldLabel} htmlFor="leads-uf">{i18n.t("aeros.leads.search.state")}</label>
          <TextField id="leads-uf" select fullWidth variant="outlined" className={`${m.input} ${m.inputSmall}`}
            value={estado} onChange={(e) => { setEstado(e.target.value); setRepetida(null); }}
            SelectProps={{ native: false }}>
            {UFS.map((uf) => <MenuItem key={uf} value={uf}>{uf}</MenuItem>)}
          </TextField>
        </div>
      </div>

      <div style={{ marginTop: 12 }}>
        <label className={m.fieldLabel} htmlFor="leads-qtd">{i18n.t("aeros.leads.search.quantity")}</label>
        <TextField id="leads-qtd" type="number" fullWidth variant="outlined" className={`${m.input} ${m.inputSmall}`}
          value={quantidade} onChange={(e) => setQuantidade(e.target.value)}
          inputProps={{ min: limites.min, max: limites.max, step: 10 }} />
        <div className={`${classes.hint} ${est.ok && dentroDosLimites ? "" : classes.hintBad}`}>
          {!dentroDosLimites
            ? i18n.t("aeros.leads.search.minMax", { min: limites.min, max: limites.max })
            : est.ok
            ? i18n.t("aeros.leads.search.estimate", { quantity: formatMilhar(est.quantidade), balance: formatMilhar(est.disponivel) })
            : i18n.t("aeros.leads.search.insufficient", { balance: formatMilhar(est.disponivel) })}
        </div>
      </div>

      {repetida && (
        <div className={classes.aviso} role="alert">
          <WarningAmberIcon />
          <span>{i18n.t("aeros.leads.search.repeated", { date: moment(repetida).format("DD/MM/YYYY"), days: diasBloqueio })}</span>
        </div>
      )}
    </AerosDialog>
  );
};

export default NovaBuscaLeadsModal;
